import React, { useContext, useMemo, useState } from 'react';
import { Charterer } from '../types/ProductTypes';

type ChartererContextType = {
    charterers: Charterer[];
    setCharterers: (charterers: Charterer[]) => void;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
};

export const ChartererContext = React.createContext<ChartererContextType>(undefined!);

interface ChartererContextProviderProps {
    children: React.ReactNode;
}

export const ChartererContextProvider = ({ children }: ChartererContextProviderProps) => {
    const [charterers, setCharterers] = useState<Charterer[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    return (
        <ChartererContext.Provider
            value={{
                charterers,
                setCharterers,
                isLoading,
                setIsLoading,
            }}
        >
            {children}
        </ChartererContext.Provider>
    );
};

export const useChartererStore = () => useContext(ChartererContext);

/**
 * Hook that returns a Map from charterer ID to charterer name.
 * Memoized so it only rebuilds when the charterer list changes.
 */
export function useChartererLookup(): Map<number, string> {
    const { charterers } = useChartererStore();
    return useMemo(
        () => new Map(charterers.map((c) => [c.id, c.name])),
        [charterers]
    );
}

/**
 * Enriches ProductListItem arrays with charterer names resolved from the cached charterer list.
 * Items that already have a chartererName keep it; items with only chartererId get the name resolved.
 */
export function enrichWithChartererNames<T extends { chartererId?: number; chartererName?: string }>(
    items: T[],
    charterers: Charterer[]
): T[] {
    if (charterers.length === 0) return items;
    const lookup = new Map(charterers.map((c) => [c.id, c.name]));
    return items.map((item) => ({
        ...item,
        chartererName: item.chartererId ? lookup.get(item.chartererId) ?? item.chartererName : item.chartererName,
    }));
}
